import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowDown } from "@fortawesome/pro-regular-svg-icons/faCloudArrowDown";
import { faFile } from "@fortawesome/pro-regular-svg-icons/faFile";

import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import ArchivedElementBanner from "./ArchivedElementBanner";
import { usePublicFile } from "./useFormResultUpload";

interface Props {
  element: DocumentElementType;
}

const FileDownload: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    label?: string;
    helpText?: string;
    files?: number[];
    hidden?: boolean;
  };
  const label = data?.label || "Download";
  const helpText = data?.helpText;
  const fileIds = Array.isArray(data?.files) ? data.files : [];

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {fileIds.length === 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/25 p-3 text-sm text-muted-foreground">
          <FontAwesomeIcon icon={faFile} className="h-4 w-4" />
          No files attached
        </div>
      ) : (
        <div className="space-y-2">
          {fileIds.map((fileId) => (
            <FileDownloadRow key={fileId} fileId={fileId} />
          ))}
        </div>
      )}
      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

const FileDownloadRow: React.FC<{ fileId: number }> = ({ fileId }) => {
  const { file, loading } = usePublicFile(fileId);
  if (!file?.url) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/25 p-3 text-sm text-muted-foreground">
        <FontAwesomeIcon icon={faFile} className="h-4 w-4" />
        {loading ? "Loading file…" : "File unavailable"}
      </div>
    );
  }
  return (
    <Button asChild variant="outline" className="h-auto justify-start gap-3 py-3">
      <a
        href={file.url}
        target="_blank"
        rel="noopener noreferrer"
        download={file.name ?? undefined}
      >
        <FontAwesomeIcon
          icon={faCloudArrowDown}
          className="h-5 w-5 text-[var(--forms-accent,var(--forms-accent-default))]"
        />
        <span className="flex flex-col items-start">
          <span className="text-sm font-medium">{file.name}</span>
          <span className="text-xs text-muted-foreground">
            Click to download
          </span>
        </span>
      </a>
    </Button>
  );
};

export default FileDownload;
